export type LoginDeviceType = "desktop" | "mobile" | "tablet" | "unknown"

export type LoginEnvironmentInput = {
	coarsePointer?: boolean
	maxTouchPoints?: number
	platform?: string
	standalone?: boolean
	userAgent?: string
	userAgentDataMobile?: boolean
}

export type LoginEnvironment = {
	deviceType: LoginDeviceType
	isAndroid: boolean
	isCoarsePointer: boolean
	isDesktop: boolean
	isIOS: boolean
	isIPadOS: boolean
	isMobile: boolean
	isStandalone: boolean
	isTablet: boolean
	isTouch: boolean
	isWeChat: boolean
	maxTouchPoints: number
	platform: string
	shouldUseRedirectLogin: boolean
	userAgent: string
}

type NavigatorWithLoginSignals = Navigator & {
	standalone?: boolean
	userAgentData?: {
		mobile?: boolean
	}
}

function normalizeText(value?: string) {
	return value || ""
}

function getRuntimeInput(): LoginEnvironmentInput {
	if (typeof navigator === "undefined") {
		return {}
	}

	const nav = navigator as NavigatorWithLoginSignals
	const coarsePointer =
		typeof window !== "undefined" && typeof window.matchMedia === "function"
			? window.matchMedia("(pointer: coarse)").matches
			: undefined

	return {
		coarsePointer,
		maxTouchPoints: nav.maxTouchPoints || 0,
		platform: nav.platform || "",
		standalone: nav.standalone,
		userAgent: nav.userAgent || "",
		userAgentDataMobile: nav.userAgentData?.mobile,
	}
}

function includesAny(value: string, patterns: RegExp[]) {
	return patterns.some((pattern) => pattern.test(value))
}

export function detectLoginEnvironment(
	input: LoginEnvironmentInput = getRuntimeInput(),
): LoginEnvironment {
	const userAgent = normalizeText(input.userAgent)
	const platform = normalizeText(input.platform)
	const rawMaxTouchPoints = input.maxTouchPoints ?? 0
	const maxTouchPoints = Number.isFinite(rawMaxTouchPoints)
		? Math.max(0, rawMaxTouchPoints)
		: 0
	const isCoarsePointer = input.coarsePointer === true
	const isTouch = maxTouchPoints > 0 || isCoarsePointer

	const isWeChat = /micromessenger/i.test(userAgent)
	const isAndroid = /\bandroid\b/i.test(userAgent)
	const hasExplicitIOSDevice = /\b(iPad|iPhone|iPod)\b/i.test(userAgent)
	const hasMacintoshUserAgent = /\bMacintosh\b/i.test(userAgent)
	const hasMacPlatform = /^Mac/i.test(platform)
	const isIPadOS =
		!hasExplicitIOSDevice &&
		hasMacintoshUserAgent &&
		hasMacPlatform &&
		typeof input.standalone !== "undefined" &&
		maxTouchPoints > 2
	const isIOS = hasExplicitIOSDevice || isIPadOS

	const isTabletByUa = includesAny(userAgent, [
		/\biPad\b/i,
		/\btablet\b/i,
		/\bplaybook\b/i,
		/\bsilk\b(?!.*\bmobile\b)/i,
		/\bkindle\b/i,
		/\bnexus 7\b/i,
		/\bnexus 9\b/i,
		/\bxoom\b/i,
		/\bsm-t\d+/i,
		/\bgt-p\d+/i,
		/\bmi pad\b/i,
	])
	const isMobileByUa = includesAny(userAgent, [
		/\bMobile\b/i,
		/\biPhone\b/i,
		/\biPod\b/i,
		/\bWindows Phone\b/i,
		/\bIEMobile\b/i,
		/\bBlackBerry\b/i,
		/\bBB10\b/i,
		/\bOpera Mini\b/i,
		/\bOpera Mobi\b/i,
		/\bwebOS\b/i,
	])
	const isAndroidTablet = isAndroid && !/\bMobile\b/i.test(userAgent)
	const isTablet = isIPadOS || isTabletByUa || isAndroidTablet
	const isMobile =
		input.userAgentDataMobile === true ||
		isMobileByUa ||
		(isAndroid && !isTablet) ||
		(isIOS && !isTablet)
	const deviceType: LoginDeviceType = isTablet
		? "tablet"
		: isMobile
			? "mobile"
			: userAgent || platform || isTouch
				? "desktop"
				: "unknown"

	const shouldUseRedirectLogin =
		deviceType === "mobile" || deviceType === "tablet" || isWeChat

	return {
		deviceType,
		isAndroid,
		isCoarsePointer,
		isDesktop: deviceType === "desktop",
		isIOS,
		isIPadOS,
		isMobile,
		isStandalone: input.standalone === true,
		isTablet,
		isTouch,
		isWeChat,
		maxTouchPoints,
		platform,
		shouldUseRedirectLogin,
		userAgent,
	}
}
